import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Plus, Trash, UserCircle } from "@phosphor-icons/react";

const inputCls =
  "rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent";

export default function OwnersManager({ currentEmail }) {
  const [owners, setOwners] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/owners");
      setOwners(data.owners || []);
    } catch {
      toast.error("Could not load owners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/owners", { email: email.trim(), password, name: name.trim() });
      toast.success("Owner profile added — they can sign in at /admin immediately");
      setName("");
      setEmail("");
      setPassword("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/owners/${id}`);
      toast.success("Owner profile removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="owners-panel">
      <div>
        <span className="cms-label text-accent">Admin Access</span>
        <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">Owner Profiles</h2>
        <p className="text-muted-foreground mt-3 text-sm max-w-lg">
          Everyone listed here can sign in at <span className="text-foreground/70">/admin</span> with
          full administrative rights — same powers as you. Add your wife or a co-owner so you don't
          share one password.
        </p>
      </div>

      <form onSubmit={add} className="border border-border p-5 bg-secondary/40 space-y-4" data-testid="add-owner-form">
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="space-y-2">
            <label className="block subhead text-foreground/50 text-xs">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Dana" className={inputCls} data-testid="new-owner-name" />
          </div>
          <div className="space-y-2">
            <label className="block subhead text-foreground/50 text-xs">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dana@example.com" className={inputCls} data-testid="new-owner-email" required />
          </div>
          <div className="space-y-2">
            <label className="block subhead text-foreground/50 text-xs">Password</label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6 characters" className={inputCls} data-testid="new-owner-password" required />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="add-owner-btn" className="inline-flex items-center gap-2 bg-foreground text-primary-foreground pl-5 pr-6 py-3 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60">
          <Plus size={15} weight="bold" /> {saving ? "Adding…" : "Add owner"}
        </button>
      </form>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="space-y-px bg-border border border-border" data-testid="owners-list">
          {owners.map((o) => (
            <div key={o.id} className="bg-background p-5 flex items-center justify-between gap-4" data-testid={`owner-row-${o.id}`}>
              <div className="flex items-center gap-4 min-w-0">
                <UserCircle size={22} className="text-accent shrink-0" weight="duotone" />
                <div className="min-w-0">
                  <p className="font-display text-lg text-foreground truncate">{o.name || "Owner"}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    {o.email}
                    {o.email === currentEmail && <span className="text-accent"> — that's you</span>}
                  </p>
                </div>
              </div>
              <button
                onClick={() => remove(o.id)}
                disabled={o.email === currentEmail}
                data-testid={`remove-owner-${o.id}`}
                className="text-muted-foreground hover:text-red-700 transition-colors shrink-0 disabled:opacity-30 disabled:hover:text-muted-foreground"
                title={o.email === currentEmail ? "You can't remove your own profile" : "Remove owner"}
              >
                <Trash size={17} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
